import React, { useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Modal,
  Paper,
  Fade,
  Typography
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import { URL } from "../config";
import AddPackageModal from "./AddPackageModal";

const PackageList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editPackage, setEditPackage] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchAll = async () => {
    try {
      const [pkgRes, typeRes, subTypeRes] = await Promise.all([
        axios.get(`${URL}/packages`),
        axios.get(`${URL}/packagetypes`),
        axios.get(`${URL}/packagesubtypes`),
      ]);

      const formatted = pkgRes.data.map((p) => {
        const type = typeRes.data.find(
          (t) => t.packageTypeId === p.fkpackageTypeId
        );
        const sub = subTypeRes.data.find(
          (s) => s.packageSubTypeId === p.fkpackageSubTypeId
        );
        return {
          ...p,
          packageTypeName: type?.packageTypeName || "-",
          packageSubTypeName: sub?.packageSubTypeName || "-",
        };
      });

      setDataSource(formatted);
    } catch {
      toast.error("Failed to fetch packages");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);
const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this package?")) return;

  try {
    await axios.delete(`${URL}/packages/${id}`);
    toast.success("Package deleted successfully");
    fetchAll(); 
  } catch (err) {
    toast.error("Failed to delete package");
  }
};

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Package List");
    worksheet.columns = [
      { header: "SrNo.", key: "packageId", width: 10 },
      { header: "Package Type", key: "packageTypeName", width: 20 },
      { header: "Package SubType", key: "packageSubTypeName", width: 20 },
      { header: "Package Title", key: "packageTitle", width: 25 },
      { header: "Package Details", key: "packageDetails", width: 40 },
      { header: "Package Price", key: "packagePrice", width: 15 },
    ];
    dataSource.forEach((item) => worksheet.addRow(item));
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Package_List.xlsx");
  };


const handleDownloadPDF = () => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(18);
  doc.text("Package List", 40, 40);

  const head = [["SrNo.", "Package Type", "Package SubType", "Package Title", "Package Details", "Package Price"]];

  const body = dataSource.map((p) => [
    p.packageId,
    p.packageTypeName,
    p.packageSubTypeName,
    p.packageTitle,
    p.packageDetails,
    p.packagePrice ?? "-",
  ]);

  autoTable(doc, {
    startY: 60,
    head,
    body,
    headStyles: { fillColor: [23, 66, 83], textColor: "#fff" },
    styles: { fontSize: 10, cellPadding: 6 },
    theme: "grid",
  });

  doc.save("Package_List.pdf");
};


const handlePrint = () => {
  const tableRows = dataSource
    .map(
      (p) => `
      <tr>
        <td>${p.packageId}</td>
        <td>${p.packageTypeName}</td>
        <td>${p.packageSubTypeName}</td>
        <td>${p.packageTitle}</td>
        <td>${p.packageDetails}</td>
        <td>${p.packagePrice ?? "-"}</td>
      </tr>
    `
    )
    .join("");

  const style = `
    <style>
      @media print {
        @page { size: landscape; margin: 18mm; }
        body { -webkit-print-color-adjust: exact !important; font-family: Arial, sans-serif; }
      }
      table { width: 100%; border-collapse: collapse; font-size: 14px; }
      th { background: #174253; color: #fff; padding: 10px; text-align: left; border-bottom: 2px solid #174253; }
      td { padding: 10px; border-top: 1px solid #dcdcdc; border-bottom: 1px solid #dcdcdc; }
      tr:nth-child(even) td { background: #f5f5f5; }
      h1 { text-align: center; color: #174253; margin-bottom: 20px; font-size: 22px; }
    </style>`;

  const html = `
    <html>
      <head><title>Package List</title>${style}</head>
      <body>
        <h1>Package List</h1>
        <table>
          <thead>
            <tr>
              <th>SrNo.</th>
              <th>Package Type</th>
              <th>Package SubType</th>
              <th>Package Title</th>
              <th>Package Details</th>
              <th>Package Price</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
    </html>`;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(html);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
};



  const columns = useMemo(
    () => [
      {
        header: "Actions",
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={() => { setEditPackage(row.original); setModalVisible(true); }}>
              <EditIcon sx={{ color: "#174253" }} />
            </IconButton>
           <IconButton
        onClick={() => handleDelete(row.original.packageId)}
      >
        <DeleteIcon sx={{ color: "#d32f2f" }} />
      </IconButton>
          </Box>
        ),
      },
      { accessorKey: "packageId", header: "SrNo." },
      { accessorKey: "packageTypeName", header: "Package Type" },
      { accessorKey: "packageSubTypeName", header: "Package SubType" },
      { accessorKey: "packageTitle", header: "Package Title" },
     {
  accessorKey: "packageDetails",
  header: "Package Details",
  size: 300,
  Cell: ({ row }) => {
    const details = row.original.packageDetails;

    return (
      <Typography
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 3,  
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: "14px",
          lineHeight: 1.4,
        }}
      >
        {details}
      </Typography>
    );
  },
}
,
      {
        accessorKey: "packagePrice",
        header: "Package Price",
        Cell: ({ row }) => (
          <strong style={{ color: "#174253" }}>₹ {row.original.packagePrice ?? "-"}</strong>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop: 2, p: { xs: 1, md: 0 }, overflow: "hidden" }}>
  
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pr: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}>
          Package List
        </Box>
        <Button
          sx={{ borderRadius: 25, background: "linear-gradient(90deg, #0b8793, #174253)", color: "white", "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" } }}
          onClick={() => { setEditPackage(null); setModalVisible(true); }}
        >
          Add Package
        </Button>
      </Box>

 
      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px", position: "relative", width: "100%", marginTop: "10px" }}>
        <div style={{ position: "absolute", top: 10, right: 20, left: 20, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Tooltip title="Download Excel"><IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}><GetAppIcon /></IconButton></Tooltip>
            <Tooltip title="Download PDF"><IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}><PictureAsPdfIcon /></IconButton></Tooltip>
            <Tooltip title="Print"><IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}><PrintIcon /></IconButton></Tooltip>
          </div>
          <TextField
            size="small"
            placeholder="Search packages..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
            sx={{ width: { xs: 200, sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: 25, height: 38, backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
          />
        </div>

        <MaterialReactTable
  columns={columns}
  data={
    [...dataSource]
      .reverse()    
      .filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalFilter.toLowerCase())
      )
  }

  enableColumnActions={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableColumnFilters={false}
  enableHiding={false}
  enableGlobalFilter={false}

  muiTablePaperProps={{
    sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
  }}

  muiTableContainerProps={{
    sx: {
      backgroundColor: "#fff",
      width: "100%",
      overflowX: "auto",
      borderRadius: "12px",
      "&::-webkit-scrollbar": { height: "6px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#e0e3ea",
        borderRadius: "10px",
      },
    },
  }}

  muiTableHeadCellProps={{
    sx: {
      backgroundColor: "#e0e0e0ff",
      color: "#393e4f",
      fontWeight: 600,
      fontSize: "16px",
      borderBottom: "0.5px solid #e3e5eb",
      borderRight: "none",
      borderLeft: "none",
      textAlign: "left",
      height: "63px",
      paddingTop: "23px",
      marginTop: "15px",
    },
  }}

  muiTableBodyRowProps={{
    sx: {
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#eff6fe" },
      "& td": {
        fontSize: { xs: "12px", sm: "14px" },
        p: "8px",
        borderBottom: "0.5px solid #e3e5eb",
        borderRight: "none",
        borderLeft: "none",
        height: "54px",
      },
    },
  }}

  initialState={{
    pagination: {
      pageIndex: 0,
      pageSize: 100, 
    },
  }}
/>

      </Box>

    
      <Modal open={modalVisible} onClose={() => setModalVisible(false)} closeAfterTransition BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}>
        <Fade in={modalVisible}>
          <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "transparent", outline: "none" }}>
            <AddPackageModal packageData={editPackage} onClose={() => setModalVisible(false)} refresh={fetchAll} />
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default PackageList;
